import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  Search,
  Filter,
  Plus,
  AlertTriangle,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Award,
  Layers,
  Power,
  RotateCcw,
  FileSpreadsheet,
  Users,
  Eye,
  ShieldAlert,
  GraduationCap
} from 'lucide-react';
import { SchoolAcademicProfileView } from './SchoolAcademicProfileView';

interface SchoolRecord {
  id: number;
  school_code: string;
  school_name: string;
  block: string;
  category: string;
  address?: string;
  official_email?: string;
  principal_name?: string;
  principal_mobile?: string;
  status: 'active' | 'inactive';
  deactivation_reason?: string;
  deactivation_remarks?: string;
  deactivated_at?: string;
  total_students: number;
  total_assessments: number;
  total_sat_records: number;
  total_teachers: number;
}

export const SchoolManagementPage: React.FC = () => {
  const { fetchWithAuth } = useAuth();

  const [schools, setSchools] = useState<SchoolRecord[]>([]);
  const [summary, setSummary] = useState({ total: 161, active: 161, inactive: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'inactive'>('active');
  const [selectedBlock, setSelectedBlock] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Drilldown / Profile View
  const [inspectSchoolId, setInspectSchoolId] = useState<number | null>(null);

  // Modals
  const [deactivateModalSchool, setDeactivateModalSchool] = useState<SchoolRecord | null>(null);
  const [deactivateReason, setDeactivateReason] = useState<string>('School Merged');
  const [deactivateRemarks, setDeactivateRemarks] = useState<string>('');
  const [deactivating, setDeactivating] = useState<boolean>(false);

  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [newSchool, setNewSchool] = useState({
    school_name: '',
    school_code: '',
    block: 'Nuh',
    category: 'Senior Secondary',
    district: 'Nuh',
    state: 'Haryana',
    address: '',
    official_email: '',
    principal_name: '',
    principal_mobile: ''
  });
  const [adding, setAdding] = useState<boolean>(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const blocks = ['Nuh', 'Nagina', 'Taoru', 'Ferozepur Jhirka', 'Punhana'];
  const categories = ['Senior Secondary', 'High School', 'Middle School', 'Primary'];

  const reasons = [
    'School Closed',
    'School Merged',
    'School Shifted',
    'School Code Changed',
    'School Transferred',
    'Duplicate School',
    'Other'
  ];

  const fetchSchools = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        status: activeTab,
        block: selectedBlock,
        category: selectedCategory,
        search: searchTerm,
        page: page.toString(),
        limit: '15'
      });

      const res = await fetchWithAuth(`/api/deo/schools-management?${params.toString()}`).then(r => r.json());
      if (res.success && res.data) {
        setSchools(res.data.schools || []);
        setSummary(res.data.summary || { total: 0, active: 0, inactive: 0 });
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalCount(res.data.pagination?.total || 0);
      } else {
        setError(res.message || 'Failed to load school management records');
      }
    } catch (err: any) {
      setError(err.message || 'Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, [activeTab, selectedBlock, selectedCategory, page]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchSchools();
  };

  const handleDeactivate = async () => {
    if (!deactivateModalSchool) return;
    if (!deactivateReason.trim()) {
      alert('Please select an official reason for deactivation.');
      return;
    }

    setDeactivating(true);
    try {
      const res = await fetchWithAuth(`/api/deo/schools-management/${deactivateModalSchool.id}/deactivate`, {
        method: 'POST',
        body: JSON.stringify({
          reason: deactivateReason,
          remarks: deactivateRemarks
        })
      }).then(r => r.json());

      if (res.success) {
        setActionSuccess(res.message);
        setDeactivateModalSchool(null);
        setDeactivateRemarks('');
        fetchSchools();
      } else {
        alert(res.message || 'Failed to deactivate school.');
      }
    } catch (e: any) {
      alert(e.message || 'Server connection error.');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (school: SchoolRecord) => {
    const confirmReactivate = window.confirm(
      `Reactivate school "${school.school_name}" (${school.school_code}) back to active operational status?`
    );
    if (!confirmReactivate) return;

    try {
      const res = await fetchWithAuth(`/api/deo/schools-management/${school.id}/reactivate`, {
        method: 'POST'
      }).then(r => r.json());

      if (res.success) {
        setActionSuccess(res.message);
        fetchSchools();
      } else {
        alert(res.message || 'Failed to reactivate school.');
      }
    } catch (e: any) {
      alert(e.message || 'Server connection error.');
    }
  };

  const handleCreateSchool = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSchool.school_name.trim() || !newSchool.school_code.trim()) {
      alert('School Name and School Code are required.');
      return;
    }

    setAdding(true);
    try {
      const res = await fetchWithAuth('/api/deo/schools-management/create', {
        method: 'POST',
        body: JSON.stringify(newSchool)
      }).then(r => r.json());

      if (res.success) {
        setActionSuccess(res.message);
        setAddModalOpen(false);
        setNewSchool({
          school_name: '',
          school_code: '',
          block: 'Nuh',
          category: 'Senior Secondary',
          district: 'Nuh',
          state: 'Haryana',
          address: '',
          official_email: '',
          principal_name: '',
          principal_mobile: ''
        });
        fetchSchools();
      } else {
        alert(res.message || 'Failed to create school.');
      }
    } catch (e: any) {
      alert(e.message || 'Server connection error.');
    } finally {
      setAdding(false);
    }
  };

  // If viewing school profile, render SchoolAcademicProfileView
  if (inspectSchoolId !== null) {
    return (
      <SchoolAcademicProfileView
        schoolId={inspectSchoolId}
        onBack={() => setInspectSchoolId(null)}
        onSelectSchool={(id) => setInspectSchoolId(id)}
      />
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Header Banner */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-[#0F2C59] text-amber-400 rounded-lg">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Official School Management & Registry</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                District Education Office, Nuh • Active school directory, soft-deactivation & administrative lifecycle
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={fetchSchools}
            className="flex items-center space-x-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg border border-slate-300 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            onClick={() => setAddModalOpen(true)}
            className="flex items-center space-x-1.5 px-3.5 py-1.5 bg-[#0F2C59] hover:bg-[#1E3A8A] text-white text-xs font-bold rounded-lg transition shadow-2xs"
          >
            <Plus className="w-4 h-4 text-amber-400" />
            <span>Add New School</span>
          </button>
        </div>
      </div>

      {/* Success Notification Alert */}
      {actionSuccess && (
        <div className="bg-emerald-50 border border-emerald-300 p-3.5 rounded-xl text-xs text-emerald-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{actionSuccess}</span>
          </div>
          <button onClick={() => setActionSuccess(null)} className="text-emerald-600 hover:text-emerald-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 2. Status Summary Tabs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={() => { setActiveTab('active'); setPage(1); }}
          className={`p-4 rounded-xl border text-left transition flex items-center justify-between ${
            activeTab === 'active'
              ? 'bg-emerald-50/80 border-emerald-500 ring-2 ring-emerald-400 shadow-xs'
              : 'bg-white border-slate-200 hover:border-emerald-300'
          }`}
        >
          <div>
            <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Active Schools</div>
            <div className="text-2xl font-black text-emerald-900 mt-1">{summary.active}</div>
            <div className="text-[10px] text-emerald-600 font-medium mt-0.5">Operational in Portal & Reports</div>
          </div>
          <div className="p-2.5 bg-emerald-100 text-emerald-700 rounded-xl">
            <Building2 className="w-6 h-6" />
          </div>
        </button>

        <button
          onClick={() => { setActiveTab('inactive'); setPage(1); }}
          className={`p-4 rounded-xl border text-left transition flex items-center justify-between ${
            activeTab === 'inactive'
              ? 'bg-rose-50/80 border-rose-500 ring-2 ring-rose-400 shadow-xs'
              : 'bg-white border-slate-200 hover:border-rose-300'
          }`}
        >
          <div>
            <div className="text-xs font-bold text-rose-800 uppercase tracking-wider">Inactive / Deactivated</div>
            <div className="text-2xl font-black text-rose-900 mt-1">{summary.inactive}</div>
            <div className="text-[10px] text-rose-600 font-medium mt-0.5">Merged / Closed / Archived</div>
          </div>
          <div className="p-2.5 bg-rose-100 text-rose-700 rounded-xl">
            <Power className="w-6 h-6" />
          </div>
        </button>

        <button
          onClick={() => { setActiveTab('all'); setPage(1); }}
          className={`p-4 rounded-xl border text-left transition flex items-center justify-between ${
            activeTab === 'all'
              ? 'bg-blue-50/80 border-blue-500 ring-2 ring-blue-400 shadow-xs'
              : 'bg-white border-slate-200 hover:border-blue-300'
          }`}
        >
          <div>
            <div className="text-xs font-bold text-blue-800 uppercase tracking-wider">All Master Schools</div>
            <div className="text-2xl font-black text-blue-900 mt-1">{summary.total}</div>
            <div className="text-[10px] text-blue-600 font-medium mt-0.5">Total Historical Registry</div>
          </div>
          <div className="p-2.5 bg-blue-100 text-blue-700 rounded-xl">
            <Layers className="w-6 h-6" />
          </div>
        </button>
      </div>

      {/* 3. Filters & Search Box */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search by School Name, School Code, or Block..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0F2C59]"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap text-xs">
            <div className="flex items-center space-x-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
              <span className="font-bold text-slate-500">Block:</span>
              <select
                value={selectedBlock}
                onChange={(e) => { setSelectedBlock(e.target.value); setPage(1); }}
                className="bg-transparent font-bold text-[#0F2C59] focus:outline-none"
              >
                <option value="all">All Blocks</option>
                {blocks.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>

            <div className="flex items-center space-x-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
              <span className="font-bold text-slate-500">Category:</span>
              <select
                value={selectedCategory}
                onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
                className="bg-transparent font-bold text-[#0F2C59] focus:outline-none"
              >
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <button
              type="submit"
              className="px-3.5 py-1.5 bg-[#0F2C59] hover:bg-[#1E3A8A] text-white font-bold rounded-lg transition text-xs"
            >
              Apply Filter
            </button>
          </div>
        </form>
      </div>

      {/* 4. Schools Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-xs">
          <div className="font-bold text-slate-800">
            Showing <span className="text-blue-900">{schools.length}</span> of <span className="text-blue-900">{totalCount}</span> Schools in View
          </div>
          <div className="text-[11px] text-slate-500">
            Status Filter: <strong className="uppercase">{activeTab}</strong>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-3 border-[#0F2C59] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-xs font-semibold text-slate-600">Loading schools registry...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center bg-rose-50 text-rose-800 text-xs font-bold">
            {error}
          </div>
        ) : schools.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-xs">
            No schools found matching the selected criteria.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                  <th className="py-3 px-3.5 font-mono">School Code</th>
                  <th className="py-3 px-4">School Name</th>
                  <th className="py-3 px-3.5">Block</th>
                  <th className="py-3 px-3.5">Category</th>
                  <th className="py-3 px-3 text-center">Students</th>
                  <th className="py-3 px-3 text-center">Assessments</th>
                  <th className="py-3 px-3 text-center">SAT Marks</th>
                  <th className="py-3 px-3.5 text-center">Operational Status</th>
                  <th className="py-3 px-4 text-center">Administrative Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                {schools.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-3.5 font-mono font-bold text-slate-700">{s.school_code}</td>
                    <td className="py-3 px-4 font-bold text-slate-900">
                      <button
                        onClick={() => setInspectSchoolId(s.id)}
                        className="text-left hover:underline text-[#0F2C59] flex items-center space-x-1"
                        title="Inspect Full Academic Performance Profile"
                      >
                        <span>{s.school_name}</span>
                      </button>
                      {s.deactivation_reason && (
                        <div className="text-[10px] text-rose-600 font-normal mt-0.5">
                          Reason: {s.deactivation_reason} {s.deactivated_at ? `(${new Date(s.deactivated_at).toLocaleDateString()})` : ''}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3.5 text-slate-700">{s.block}</td>
                    <td className="py-3 px-3.5 text-slate-600 text-[11px]">{s.category}</td>
                    <td className="py-3 px-3 text-center font-bold">{s.total_students}</td>
                    <td className="py-3 px-3 text-center text-slate-500">{s.total_assessments}</td>
                    <td className="py-3 px-3 text-center text-slate-500">{s.total_sat_records}</td>
                    <td className="py-3 px-3.5 text-center">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center space-x-1 ${
                        s.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                          : 'bg-rose-100 text-rose-800 border border-rose-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s.status === 'active' ? 'bg-emerald-600' : 'bg-rose-600'}`} />
                        <span className="capitalize">{s.status}</span>
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-1.5">
                        {/* Profile Button */}
                        <button
                          onClick={() => setInspectSchoolId(s.id)}
                          className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-bold text-[11px] transition flex items-center space-x-1"
                          title="View Complete Academic Performance Profile"
                        >
                          <Eye className="w-3 h-3 text-blue-600" />
                          <span>Profile</span>
                        </button>

                        {/* Deactivate Button (Active Schools) */}
                        {s.status === 'active' ? (
                          <button
                            onClick={() => {
                              setDeactivateModalSchool(s);
                              setDeactivateReason('School Merged');
                              setDeactivateRemarks('');
                            }}
                            className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded font-bold text-[11px] transition flex items-center space-x-1"
                            title="Soft-deactivate school from active monitoring lists (Preserves all historical records)"
                          >
                            <Power className="w-3 h-3 text-rose-600" />
                            <span>Deactivate</span>
                          </button>
                        ) : (
                          /* Reactivate Button (Inactive Schools) */
                          <button
                            onClick={() => handleReactivate(s)}
                            className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded font-bold text-[11px] transition flex items-center space-x-1"
                            title="Restore school to active operations"
                          >
                            <RotateCcw className="w-3 h-3 text-emerald-600" />
                            <span>Reactivate</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        <div className="p-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs">
          <div className="text-slate-600 font-medium">
            Page <span className="font-bold text-slate-900">{page}</span> of <span className="font-bold text-slate-900">{totalPages}</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 bg-white border border-slate-300 rounded-lg text-slate-700 font-bold hover:bg-slate-100 disabled:opacity-40 transition"
            >
              <ChevronLeft className="w-3.5 h-3.5 inline" /> Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 bg-white border border-slate-300 rounded-lg text-slate-700 font-bold hover:bg-slate-100 disabled:opacity-40 transition"
            >
              Next <ChevronRight className="w-3.5 h-3.5 inline" />
            </button>
          </div>
        </div>
      </div>

      {/* 5. DEACTIVATE SCHOOL SAFETY CONFIRMATION MODAL */}
      {deactivateModalSchool && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden border border-slate-200">
            {/* Modal Header */}
            <div className="p-4 bg-gradient-to-r from-rose-700 to-rose-900 text-white flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="p-2 bg-white/10 rounded-lg">
                  <ShieldAlert className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Deactivate School Record</h3>
                  <p className="text-[11px] text-rose-200">Administrative Soft-Disable & Archive</p>
                </div>
              </div>
              <button
                onClick={() => setDeactivateModalSchool(null)}
                className="p-1 text-white/80 hover:text-white rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 text-xs">
              {/* Critical Safety Notice */}
              <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-3.5 text-emerald-900 flex items-start space-x-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="leading-relaxed text-[11px]">
                  <strong>Non-Destructive Safety Guarantee:</strong> Deactivating this school will remove it from active monitoring dashboards and entry lists. <strong>Zero historical data will be deleted.</strong> All student records, marks, and attendance remain 100% intact.
                </div>
              </div>

              {/* School Summary Ledger */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-2">
                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Target Institution Details</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500 font-medium">School Name:</span>
                    <div className="font-bold text-slate-900">{deactivateModalSchool.school_name}</div>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">School Code:</span>
                    <div className="font-bold text-[#0F2C59] font-mono">{deactivateModalSchool.school_code}</div>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Block:</span>
                    <div className="font-bold text-slate-800">{deactivateModalSchool.block}</div>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Category:</span>
                    <div className="font-bold text-slate-800">{deactivateModalSchool.category}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-2 mt-2 border-t border-slate-200 text-center text-[11px]">
                  <div className="p-1.5 bg-white rounded border border-slate-200">
                    <span className="text-slate-500 text-[10px]">Total Students</span>
                    <div className="font-black text-slate-900">{deactivateModalSchool.total_students}</div>
                  </div>
                  <div className="p-1.5 bg-white rounded border border-slate-200">
                    <span className="text-slate-500 text-[10px]">Assessments</span>
                    <div className="font-black text-slate-900">{deactivateModalSchool.total_assessments}</div>
                  </div>
                  <div className="p-1.5 bg-white rounded border border-slate-200">
                    <span className="text-slate-500 text-[10px]">SAT Records</span>
                    <div className="font-black text-slate-900">{deactivateModalSchool.total_sat_records}</div>
                  </div>
                </div>
              </div>

              {/* Reason Selector */}
              <div className="space-y-1.5">
                <label className="font-bold text-slate-700 flex items-center justify-between">
                  <span>Deactivation Reason (Mandatory) *</span>
                  <span className="text-[10px] text-slate-400">Select administrative basis</span>
                </label>
                <select
                  value={deactivateReason}
                  onChange={(e) => setDeactivateReason(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-bold text-slate-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-rose-500"
                >
                  {reasons.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {/* Remarks Textarea */}
              <div className="space-y-1.5">
                <label className="font-bold text-slate-700">Official Remarks / Order Reference (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="e.g., Merged with GSSS Taoru as per DEO order No. 402/2026..."
                  value={deactivateRemarks}
                  onChange={(e) => setDeactivateRemarks(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 text-xs text-slate-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-rose-500"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end space-x-2">
              <button
                onClick={() => setDeactivateModalSchool(null)}
                disabled={deactivating}
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivate}
                disabled={deactivating}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg shadow-sm transition flex items-center space-x-1.5 disabled:opacity-50"
              >
                <Power className="w-3.5 h-3.5" />
                <span>{deactivating ? 'Deactivating...' : 'Confirm Deactivation'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 6. ADD SCHOOL MODAL */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden border border-slate-200">
            <div className="p-4 bg-[#0F2C59] text-white flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold text-sm">Register New School in District Database</h3>
              </div>
              <button onClick={() => setAddModalOpen(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateSchool} className="p-5 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700">School Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Government Senior Secondary School,..."
                    value={newSchool.school_name}
                    onChange={(e) => setNewSchool({ ...newSchool, school_name: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 text-slate-900 focus:outline-none focus:bg-white"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700">School Code *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. HR-NUH-0162"
                    value={newSchool.school_code}
                    onChange={(e) => setNewSchool({ ...newSchool, school_code: e.target.value.toUpperCase() })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 font-mono font-bold text-slate-900 focus:outline-none focus:bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700">Block *</label>
                  <select
                    value={newSchool.block}
                    onChange={(e) => setNewSchool({ ...newSchool, block: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 font-bold text-slate-900 focus:outline-none focus:bg-white"
                  >
                    {blocks.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-bold text-slate-700">Category *</label>
                  <select
                    value={newSchool.category}
                    onChange={(e) => setNewSchool({ ...newSchool, category: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 font-bold text-slate-900 focus:outline-none focus:bg-white"
                  >
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700">Principal Name</label>
                  <input
                    type="text"
                    placeholder="Principal / Incharge Name"
                    value={newSchool.principal_name}
                    onChange={(e) => setNewSchool({ ...newSchool, principal_name: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 text-slate-900 focus:outline-none focus:bg-white"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700">Official Contact Number</label>
                  <input
                    type="text"
                    placeholder="10-digit mobile"
                    value={newSchool.principal_mobile}
                    onChange={(e) => setNewSchool({ ...newSchool, principal_mobile: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 text-slate-900 focus:outline-none focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700">Official Email</label>
                <input
                  type="email"
                  placeholder="school@haryana.gov.in"
                  value={newSchool.official_email}
                  onChange={(e) => setNewSchool({ ...newSchool, official_email: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 text-slate-900 focus:outline-none focus:bg-white"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700">Address / Location</label>
                <input
                  type="text"
                  placeholder="Village / Town, District Nuh, Haryana"
                  value={newSchool.address}
                  onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 mt-1 text-slate-900 focus:outline-none focus:bg-white"
                />
              </div>

              <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="px-4 py-2 bg-[#0F2C59] hover:bg-[#1E3A8A] text-white text-xs font-bold rounded-lg shadow-sm disabled:opacity-50"
                >
                  {adding ? 'Creating...' : 'Register School'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
